import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { getRpcUrlsForChain } from '@/chains';
import { CONTRACT_ADDRESSES } from '@/contracts/config';

const REFRESH_INTERVAL_MS = 60 * 1000;

const PRICE_CONSUMER_ABI = [
    'function maxStaleness() view returns (uint256)',
    'function ref() view returns (address)',
];

/**
 * Band StdReference queried directly instead of through CotiPriceConsumer:
 * the consumer's getters all revert with StaleOracleData once a feed passes
 * maxStaleness, while the raw reference always returns the last pushed rate
 * and its timestamp — which is exactly what a staleness monitor needs.
 */
const STD_REFERENCE_ABI = [
    'function getReferenceData(string base, string quote) view returns (uint256 rate, uint256 lastUpdatedBase, uint256 lastUpdatedQuote)',
];

/** Bridge token symbol → Band oracle base (mirrors the plugin's SYMBOL_TO_ORACLE_BASE). */
const TOKEN_ORACLE_BASES = [
    { symbol: 'COTI', base: 'COTI' },
    { symbol: 'WETH', base: 'ETH' },
    { symbol: 'WBTC', base: 'WBTC' },
    { symbol: 'USDT', base: 'USDT' },
    { symbol: 'USDC.e', base: 'USDC' },
    { symbol: 'WADA', base: 'ADA' },
    // Consumer base is uppercase GCOTI (lowercase gCOTI reverts).
    { symbol: 'gCOTI', base: 'GCOTI' },
    { symbol: 'NIGHT', base: 'NIGHT' },
] as const;

export interface BandOracleTokenStatus {
    symbol: string;
    /** Band base symbol the consumer queries for this token (quote is always USD). */
    base: string;
    priceUsd: number | null;
    lastUpdated: Date | null;
    /** True when the feed is older than the consumer's maxStaleness (price reads revert on-chain). */
    isStale: boolean;
    error: string | null;
}

export interface BandOracleStatus {
    consumerAddress: string;
    refAddress: string;
    maxStalenessSeconds: number;
    tokens: BandOracleTokenStatus[];
    fetchedAt: Date;
}

export interface UseBandOracleStatusReturn {
    status: BandOracleStatus | null;
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

function getConsumerAddress(chainId: number | null): string | null {
    if (chainId == null) return null;
    return (CONTRACT_ADDRESSES as Record<number, Record<string, string>>)[chainId]?.CotiPriceConsumer ?? null;
}

async function fetchStatus(chainId: number, consumerAddress: string): Promise<BandOracleStatus> {
    const rpcUrls = getRpcUrlsForChain(chainId);
    let lastError: unknown;

    for (const url of rpcUrls) {
        const provider = new ethers.JsonRpcProvider(url, undefined, { staticNetwork: true });
        try {
            const consumer = new ethers.Contract(consumerAddress, PRICE_CONSUMER_ABI, provider);
            const [maxStaleness, refAddress] = await Promise.all([
                consumer.maxStaleness() as Promise<bigint>,
                consumer.ref() as Promise<string>,
            ]);
            const ref = new ethers.Contract(refAddress, STD_REFERENCE_ABI, provider);

            const nowSeconds = Math.floor(Date.now() / 1000);
            const tokens: BandOracleTokenStatus[] = await Promise.all(
                TOKEN_ORACLE_BASES.map(async ({ symbol, base }) => {
                    try {
                        const [rate, lastUpdatedBase] = await ref.getReferenceData(base, 'USD') as [bigint, bigint, bigint];
                        return {
                            symbol,
                            base,
                            priceUsd: Number(ethers.formatEther(rate)),
                            lastUpdated: new Date(Number(lastUpdatedBase) * 1000),
                            isStale: BigInt(nowSeconds) - lastUpdatedBase > maxStaleness,
                            error: null,
                        };
                    } catch (err: unknown) {
                        const message = err instanceof Error ? err.message : 'feed unavailable';
                        return { symbol, base, priceUsd: null, lastUpdated: null, isStale: true, error: message };
                    }
                }),
            );

            return {
                consumerAddress,
                refAddress,
                maxStalenessSeconds: Number(maxStaleness),
                tokens,
                fetchedAt: new Date(),
            };
        } catch (err) {
            lastError = err;
        } finally {
            provider.destroy();
        }
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to reach the Band oracle');
}

/**
 * Per-token Band oracle feed status for the chain's CotiPriceConsumer.
 * Returns a null status on chains without a consumer (PoD chains); pass
 * null to disable. Auto-refreshes every minute.
 */
export const useBandOracleStatus = (chainId: number | null): UseBandOracleStatusReturn => {
    const consumerAddress = getConsumerAddress(chainId);
    const [status, setStatus] = useState<BandOracleStatus | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(consumerAddress != null);
    const [error, setError] = useState<string | null>(null);
    /** Bumped on each fetch so a slow prior-chain response cannot overwrite the new chain. */
    const requestIdRef = useRef(0);

    const refresh = useCallback(async () => {
        if (chainId == null || !consumerAddress) {
            requestIdRef.current += 1;
            setStatus(null);
            setIsLoading(false);
            setError(null);
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        try {
            const next = await fetchStatus(chainId, consumerAddress);
            if (requestId !== requestIdRef.current) return;
            setStatus(next);
            setError(null);
        } catch (err: unknown) {
            if (requestId !== requestIdRef.current) return;
            setError(err instanceof Error ? err.message : 'Failed to fetch oracle status');
            setStatus(null);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [chainId, consumerAddress]);

    useEffect(() => {
        // Drop previous chain's rows immediately on network switch.
        setStatus(null);
        setError(null);
        setIsLoading(consumerAddress != null);
        void refresh();
        const interval = setInterval(() => {
            void refresh();
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [refresh, consumerAddress]);

    return { status, isLoading, error, refresh };
};
